import { parseWorkflowId } from './parseWorkflowId';

/** What the `/view/:contentId` route should open, decoded from its query string. */
export type ViewTarget =
  | { readonly kind: 'read-draft' }
  | { readonly kind: 'read-publication'; readonly workflowId?: number }
  | { readonly kind: 'unsupported' };

/**
 * Decode a `/view` query string into a {@link ViewTarget}. `mode=publication`
 * opens a published version — optionally pinned to a specific `workflowId`.
 * `mode=draft` opens the live draft. No `mode` also opens the draft for now, so
 * older links keep working; links should state `mode=draft` explicitly. A draft is always the live version, so any `workflowId` that isn't
 * paired with `mode=publication` is `unsupported` — including a workflow of a
 * kind `/view` can't render (e.g. finalizing).
 *
 * @throws when `workflowId` is present but not a number.
 */
export function parseViewTarget(search: string): ViewTarget {
  const params = new URLSearchParams(search);
  const workflowId = parseWorkflowId(params.get('workflowId'));
  const mode = params.get('mode');

  // mode=publication opens a published version, with or without a pinned workflowId.
  if (mode === 'publication') return { kind: 'read-publication', workflowId };

  // A workflowId without mode=publication is unsupported — a draft can't be pinned
  // to a version, and for any other mode we can't infer the kind.
  if (workflowId !== undefined) return { kind: 'unsupported' };

  // mode=draft opens the live draft. No mode still falls back to the draft for older links.
  return { kind: 'read-draft' };
}
